'use client';

import { EnergyType, ProductType, PlanFlow } from '@energy-retailer/constants';
import { validCLNTC, validNTC } from '@energy-retailer/schema';
import { getCookie } from 'cookies-next';
import { isEmpty } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { trackAnalyticsEvent } from '@/app/lib/analytics';
import { usePlans } from '@/app/lib/hooks/use-plans';
import { AlertBox } from '@/components/alert';
import { Button } from '@/components/button';
import { Loader } from '@/components/loader';
import { ProductPlanCard } from '@/components/product-plan-card';
import { Text } from '@/components/text';
import { ANALYTICS_EVENTS, PROMO_ID_COOKIE } from '@/constants';
import { useJoinFormStore } from '@/stores/join-form-store/join-form-store';

import { OneBigSwitchModal } from '../one-big-switch-modal';

import { useIsPlanSearching } from './context';
import EstimatedPriceRow from './estimated-price-row';
import { OptOutOfFuelButton } from './opt-out-of-fuel-button';

import type { FilteringSchemaType } from './form-schema';
import type { Step } from '@/stores/join-form-store/steps';
import type {
  ElectricityDistributorSchema,
  PlanSchema,
} from '@energy-retailer/schema';

declare const window: Window & { dataLayer?: Record<string, unknown>[] };

type Props = {
  handleScroll: () => void;
  setAddAddressActive: (addAddressActive: boolean) => void;
  currentStep: Step | null;
  currentStepNumber: number;
};

const PlanListing = ({
  handleScroll,
  setAddAddressActive,
  currentStep,
  currentStepNumber,
}: Props) => {
  const userFirstSeenPlanWithPricing = useRef(false);
  const [filteredPlans, setFilteredPlans] = useState<PlanSchema[]>([]);
  const {
    property,
    getSteps,
    electricityPlanId,
    gasPlanId,
    flow,
    setPromotionId: setPromotionIdJoinFormStore,
  } = useJoinFormStore();
  const { watch, setValue } = useFormContext<FilteringSchemaType>();
  const { isPlanSearching } = useIsPlanSearching();

  const promoId = getCookie(PROMO_ID_COOKIE);
  const priceFrequency = watch('priceFrequency');
  const preferences = watch('preferences');
  const distributor = watch('distributor');

  const selectedDistributor = useMemo(() => {
    // Dont use form distributor if no address is added.
    if (distributor && property.address.full) {
      // Make sure distributor exists on new property
      if (currentStep?.fuel === EnergyType.Electricity) {
        const foundDistributor = property.electricityDistributors.find(
          ({ id }) => id === distributor
        );

        if (foundDistributor) {
          return distributor;
        }

        return property.electricityDistributors[0]?.id;
      }

      if (currentStep?.fuel === EnergyType.Gas) {
        const foundDistributor = property.gasDistributors.find(
          ({ id }) => id === distributor
        );

        if (foundDistributor) {
          return distributor;
        }

        return property.gasDistributors[0]?.id;
      }
    }

    if (currentStep?.fuel === EnergyType.Gas && property.gasDistributors[0]) {
      return property.gasDistributors[0].id;
    }

    if (
      property.electricityDistributors[0] &&
      currentStep?.fuel === EnergyType.Electricity
    ) {
      return property.electricityDistributors[0].id;
    }

    return '';
  }, [
    distributor,
    property.address.full,
    property.electricityDistributors,
    property.gasDistributors,
    currentStep?.fuel,
  ]);

  const selectedTariffCode = useMemo(() => {
    if (currentStep?.fuel === EnergyType.Gas) {
      return 'V';
    }

    const meterDistributors = property.electricityDistributors.map(
      (electricityDistributor: ElectricityDistributorSchema) =>
        electricityDistributor.id
    );

    if (
      !isEmpty(property.electricityDistributors) &&
      selectedDistributor &&
      // Used to prevent function from firing while selected ditributor changes from gas to elec distributors.
      meterDistributors.includes(selectedDistributor) &&
      currentStep?.fuel === EnergyType.Electricity
    ) {
      const meters = (
        property.electricityDistributors as ElectricityDistributorSchema[]
      ).find((value) => value.id === selectedDistributor)?.meter[
        property.customerType!
      ];

      if (meters === undefined) {
        throw new Error('No meters found for selected distributor');
      }

      const controlledLoadMeters = meters
        .filter(
          (meter) =>
            meter.controlledLoad === 'YES' || meter.controlledLoad === 'EXT'
        )
        .filter(({ networkTariffCode }) =>
          validCLNTC.includes(networkTariffCode)
        );

      const uncontrolledLoadMeters = meters
        .filter((meter) => meter.controlledLoad === 'NO')
        .filter(({ networkTariffCode }) =>
          validNTC.includes(networkTariffCode as (typeof validNTC)[number])
        );

      const ntc = uncontrolledLoadMeters[0]?.networkTariffCode || null;
      const clNTC = [
        ...new Set(
          controlledLoadMeters.map((meter) => meter.networkTariffCode)
        ),
      ];

      return [ntc, ...clNTC].filter(Boolean).join(':');
    }

    return '';
  }, [
    property.electricityDistributors,
    property.customerType,
    selectedDistributor,
    currentStep?.fuel,
  ]);

  const {
    plans,
    isFetching,
    status,
    setEnergyType,
    setDistributor,
    setNtc,
    setCustomerType,
    setPostcode,
    setPromotionId,
  } = usePlans(selectedDistributor, selectedTariffCode);

  const steps = getSteps();

  useEffect(() => {
    const filteredResults =
      steps.length === 0
        ? []
        : (plans ?? []).filter((value) => {
            if (preferences) {
              if (preferences.includes('carbonNeutral')) {
                return (
                  value.product.productType === ProductType.Sustainable ||
                  value.product.slug === 'power-offset'
                );
              }

              if (!preferences.includes('electricVehicle')) {
                return ![
                  ProductType.EvNight,
                  ProductType.EvDay,
                  ProductType.EvDaySaver,
                  ProductType.EvNightSaver,
                ].includes(value.product.productType);
              }

              if (!preferences.includes('solarPanels')) {
                return value.product.productType !== ProductType.Solar;
              }
            }

            return value;
          });

    setFilteredPlans(filteredResults);
  }, [plans, preferences, currentStep, steps.length]);

  useEffect(() => {
    setPromotionId(promoId);
  }, [promoId, setPromotionId]);

  useEffect(() => {
    setPromotionIdJoinFormStore(promoId || null);
  }, [promoId, setPromotionIdJoinFormStore]);

  useEffect(() => {
    setEnergyType(
      currentStep?.fuel === EnergyType.Electricity
        ? EnergyType.Electricity
        : EnergyType.Gas
    );
  }, [setEnergyType, currentStepNumber, currentStep]);

  useEffect(() => {
    setCustomerType(property.customerType);
  }, [setCustomerType, property.customerType]);

  useEffect(() => {
    setPostcode(property.address.postcode);
  }, [setPostcode, property.address.postcode]);

  useEffect(() => {
    setNtc(selectedTariffCode);
  }, [selectedTariffCode, setNtc, currentStepNumber]);

  useEffect(() => {
    setDistributor(selectedDistributor);
  }, [selectedDistributor, setDistributor, currentStepNumber]);

  const hasAddress = Boolean(
    filteredPlans?.find((value) => !value.hidePricing || property.address.full)
  );

  useEffect(() => {
    if (hasAddress && filteredPlans?.length && filteredPlans?.length > 0) {
      trackAnalyticsEvent(ANALYTICS_EVENTS.compareEnergyPlans.pricesDisplayed, {
        postcode: property.address.postcode,
        flow,
      });
    }
  }, [
    hasAddress,
    property.address.full,
    property.address.postcode,
    filteredPlans?.length,
    flow,
  ]);

  useEffect(() => {
    // Fire analytics when a user first sees a plan with pricing
    if (
      !userFirstSeenPlanWithPricing.current &&
      hasAddress &&
      filteredPlans?.length > 0
    ) {
      if (window.dataLayer) {
        if (flow === PlanFlow.DualFuel) {
          trackAnalyticsEvent(
            ANALYTICS_EVENTS.checkboxesSelections
              .dual_fuel_quote_flow_selected_cep,
            { postcode: property.address.postcode }
          );
        } else if (flow === PlanFlow.Electricity) {
          trackAnalyticsEvent(
            ANALYTICS_EVENTS.checkboxesSelections
              .electricity_quote_flow_selected_cep,
            { postcode: property.address.postcode }
          );
        } else if (flow === PlanFlow.Gas) {
          trackAnalyticsEvent(
            ANALYTICS_EVENTS.checkboxesSelections.gas_quote_flow_selected_cep,
            { postcode: property.address.postcode }
          );
        }
      }
      userFirstSeenPlanWithPricing.current = true;
    }
  }, [hasAddress, property.address.full, filteredPlans, flow]);

  const isPlanSelected = ({ id }: { id: string }) => {
    if (currentStep?.fuel === EnergyType.Electricity) {
      return electricityPlanId === id;
    }

    if (currentStep?.fuel === EnergyType.Gas) {
      return gasPlanId === id;
    }

    return false;
  };

  if (isFetching || isPlanSearching || status === 'pending') {
    return <Loader className="bg-gray-50 py-j lg:py-i" />;
  }

  if (!filteredPlans?.length || status === 'error') {
    return (
      <div className="grid grid-cols-12 gap-c">
        <div className="col-span-10 col-start-2">
          <AlertBox
            title="Oops!"
            buttonComponent={
              <Button
                variant="secondary"
                href="/contact-us"
              >
                Contact Us
              </Button>
            }
          >
            <Text
              as="p"
              variant="body2"
            >
              It looks like there are no available plans that meet your search
              terms, try a different search or get in touch with our friendly
              support team.
            </Text>
          </AlertBox>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-12 gap-c lg:hidden">
        <div className="col-span-10 col-start-2">
          <EstimatedPriceRow
            priceFrequency={priceFrequency}
            setValue={setValue}
          />
        </div>
      </div>
      <div className="grid grid-cols-12 gap-c">
        <div className="col-span-12 mb-2 place-items-center">
          <Text
            as="h4"
            variant="h4"
            className="lg:text-h3 text-center text-gray-700"
          >
            {filteredPlans.length} plan{filteredPlans.length > 1 ? 's' : ''}{' '}
            available based on your preferences
          </Text>
        </div>
        {filteredPlans.map((plan) => (
          <div
            key={plan.key}
            data-testid="product-plan-card-desktop"
            className="col-span-10 col-start-2"
          >
            <ProductPlanCard
              plan={plan}
              addAddress={handleScroll}
              setAddAddressActive={setAddAddressActive}
              includeSolarFeedTariff={(preferences || []).includes(
                'solarPanels'
              )}
              selected={isPlanSelected({ id: plan.planID })}
              hasAddress={hasAddress}
              priceFrequency={priceFrequency}
            />
          </div>
        ))}
      </div>
      <OneBigSwitchModal setPromotionId={setPromotionId} />
      {!(
        (currentStep?.fuel === EnergyType.Electricity && electricityPlanId) ||
        (currentStep?.fuel === EnergyType.Gas && gasPlanId)
      ) && currentStep?.fuel ? (
        <OptOutOfFuelButton fuel={currentStep?.fuel} />
      ) : undefined}
    </>
  );
};

export { PlanListing };
